import { MAX_RECIPES_PER_USER } from '@/constants/limits';
import { useAuth } from '@/hooks/use-auth';
import {
    getCachedRecipe,
    getCachedRecipes,
    invalidateRecipesCache,
    removeCachedRecipe,
    setCachedRecipe,
    setCachedRecipes,
    updateCachedRecipeInList,
} from '@/lib/recipe-cache';
import type { Recipe } from '@/lib/recipes/types';
import { supabase } from '@/lib/supabase';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

export type { Recipe };

/** Raw recipe row from Supabase */
interface RecipeRow {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  servings: number;
  prep_time_minutes: number | null;
  cook_time_minutes: number | null;
  ingredients: { name?: string; quantity?: number; unit?: string; notes?: string; category?: string }[] | string[];
  instructions: string;
  tags: string[];
  image_urls: string[];
  source_url: string | null;
  is_public: boolean;
  times_saved: number;
  created_at: string;
  updated_at: string;
}

/** Input for creating a recipe */
export interface CreateRecipeInput {
  title: string;
  description?: string;
  ingredients: string[];
  instructions: string[];
  servings?: number;
  prepTime?: number;
  cookTime?: number;
  images?: string[];
  tags?: string[];
  sourceUrl?: string;
}

/** Input for updating a recipe (all optional) */
export type UpdateRecipeInput = Partial<CreateRecipeInput>;

function rowToRecipe(row: RecipeRow): Recipe {
  const ingredients: string[] = Array.isArray(row.ingredients)
    ? row.ingredients.map((ing) => {
        if (typeof ing === 'string') return ing;
        const o = ing as { name?: string; quantity?: number; unit?: string };
        if (o.name) return o.name;
        const parts = [o.quantity, o.unit, o.name].filter(Boolean);
        return parts.length ? parts.join(' ') : JSON.stringify(ing);
      })
    : [];

  const instructions: string[] =
    typeof row.instructions === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(row.instructions);
            return Array.isArray(parsed) ? parsed : row.instructions.split('\n').filter(Boolean);
          } catch {
            return row.instructions.split('\n').filter(Boolean);
          }
        })()
      : [];

  return {
    id: row.id,
    title: row.title,
    description: row.description ?? undefined,
    ingredients,
    instructions,
    servings: row.servings ?? 4,
    prepTime: row.prep_time_minutes ?? 0,
    cookTime: row.cook_time_minutes ?? 0,
    images: row.image_urls ?? [],
    tags: row.tags ?? [],
    createdAt: new Date(row.created_at).getTime(),
    createdBy: row.user_id,
  };
}

function recipeToRow(recipe: CreateRecipeInput | UpdateRecipeInput, userId: string): Partial<RecipeRow> {
  const ing = (recipe.ingredients ?? []).map((s) =>
    typeof s === 'string' ? { name: s, quantity: null, unit: null, notes: null, category: null } : s
  );
  const instr = Array.isArray(recipe.instructions)
    ? JSON.stringify(recipe.instructions)
    : String(recipe.instructions ?? '');
  const row: Partial<RecipeRow> = {};
  if (recipe.title !== undefined) row.title = recipe.title;
  if (recipe.description !== undefined) row.description = recipe.description ?? null;
  if (recipe.servings !== undefined) row.servings = recipe.servings ?? 4;
  if (recipe.prepTime !== undefined) row.prep_time_minutes = recipe.prepTime ?? null;
  if (recipe.cookTime !== undefined) row.cook_time_minutes = recipe.cookTime ?? null;
  if (recipe.ingredients !== undefined) row.ingredients = ing;
  if (recipe.instructions !== undefined) row.instructions = instr;
  if (recipe.tags !== undefined) row.tags = recipe.tags ?? [];
  if (recipe.images !== undefined) row.image_urls = recipe.images ?? [];
  if (recipe.sourceUrl !== undefined) row.source_url = recipe.sourceUrl ?? null;
  return row;
}

/**
 * Fetch the current user's saved recipes (recipes they own).
 * Uses cache - only fetches when cache is empty or invalidated (after create/update/delete).
 */
export function useRecipes() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(
    async (forceNetwork = false) => {
      if (!user?.id) {
        setRecipes([]);
        setLoading(false);
        return;
      }

      const cached = getCachedRecipes(user.id);
      if (cached && cached.length > 0 && !forceNetwork) {
        setRecipes(cached);
        setLoading(false);
        // Keep by-id cache in sync so detail screen always has recipes when list is shown
        cached.forEach((r) => setCachedRecipe(r));
        if (__DEV__) console.log('[useRecipes] using list cache, synced', cached.length, 'recipes to by-id cache');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('recipes')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        const list = (data ?? []).map((r) => rowToRecipe(r as RecipeRow));
        setRecipes(list);
        setCachedRecipes(user.id, list);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    },
    [user?.id]
  );

  return { recipes, loading, error, refetch };
}

/**
 * Fetch a single recipe by ID.
 * Uses cache first - only fetches from network when not in cache (e.g. from list or previous fetch).
 * Always prefers live cache when returning so list->detail never triggers a request when cache is warm.
 */
export function useRecipe(id: string | undefined) {
  const cached = id ? getCachedRecipe(id) : null;
  const [recipe, setRecipe] = useState<Recipe | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);

  const fetchRecipe = useCallback(async () => {
    if (!id) return;
    const existing = getCachedRecipe(id);
    if (existing) {
      setRecipe(existing);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('recipes')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (data) {
        const r = rowToRecipe(data as RecipeRow);
        setRecipe(r);
        setCachedRecipe(r);
      } else {
        setRecipe(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setRecipe(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Sync from cache synchronously before paint so we never trigger fetch when cache is warm
  useLayoutEffect(() => {
    if (!id) {
      setRecipe(null);
      setLoading(false);
      return;
    }
    const existing = getCachedRecipe(id);
    if (existing) {
      setRecipe(existing);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const existing = getCachedRecipe(id);
    if (existing) {
      if (__DEV__) console.log('[useRecipe] cache HIT for', id.slice(0, 8), '- skipping network');
      return;
    }
    if (__DEV__) console.log('[useRecipe] cache MISS for', id.slice(0, 8), '- fetching from network');
    fetchRecipe();
  }, [id, fetchRecipe]);

  // Prefer live cache when available so we never show loading or trigger fetch when cache is warm (e.g. coming from list).
  const cachedNow = id ? getCachedRecipe(id) : null;
  const displayRecipe = cachedNow ?? recipe ?? null;
  const displayLoading = cachedNow ? false : loading;

  return { recipe: displayRecipe, loading: displayLoading, error, refetch: fetchRecipe };
}

/**
 * Mutation hook for creating a recipe.
 */
export function useCreateRecipe() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createRecipe = useCallback(
    async (input: CreateRecipeInput): Promise<Recipe | null> => {
      if (!user?.id) {
        setError(new Error('You must be signed in to create a recipe'));
        return null;
      }

      setLoading(true);
      setError(null);
      try {
        const { count, error: countError } = await supabase
          .from('recipes')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (countError) throw countError;
        if ((count ?? 0) >= MAX_RECIPES_PER_USER) {
          const e = new Error(
            `You've reached the maximum of ${MAX_RECIPES_PER_USER} recipes. Delete a recipe to add more.`
          );
          setError(e);
          throw e;
        }

        const row = recipeToRow(input, user.id) as Record<string, unknown>;
        const insert = {
          ...row,
          user_id: user.id,
        };

        const { data, error: insertError } = await supabase
          .from('recipes')
          .insert(insert)
          .select()
          .single();

        if (insertError) throw insertError;
        invalidateRecipesCache();
        if (input.sourceUrl?.trim()) {
          supabase.functions.invoke('increment-recipe-add-count', { body: { source_url: input.sourceUrl.trim() } }).catch(() => {});
        }
        return data ? rowToRecipe(data as RecipeRow) : null;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [user?.id]
  );

  return { createRecipe, loading, error };
}

/**
 * Mutation hook for updating a recipe.
 */
export function useUpdateRecipe() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateRecipe = useCallback(async (id: string, input: UpdateRecipeInput): Promise<Recipe | null> => {
    setLoading(true);
    setError(null);
    try {
      const row = recipeToRow(input, '');
      const { data, error: updateError } = await supabase
        .from('recipes')
        .update(row)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;
      const updated = data ? rowToRecipe(data as RecipeRow) : null;
      if (updated) {
        setCachedRecipe(updated);
        updateCachedRecipeInList(updated);
      }
      return updated;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateRecipe, loading, error };
}

/**
 * Mutation hook for deleting a recipe.
 */
export function useDeleteRecipe() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteRecipe = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const { error: deleteError } = await supabase.from('recipes').delete().eq('id', id);
      if (deleteError) throw deleteError;
      invalidateRecipesCache();
      removeCachedRecipe(id);
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteRecipe, loading, error };
}
